import { useMutation, useInfiniteQuery, QueryClient, InfiniteData } from '@tanstack/react-query';
import cloneDeep from 'lodash-es/cloneDeep';
import { useSelector } from 'react-redux';

import { messagesEndpoints as endpoints } from '@/api/endpoints';
import { callGet, callPost } from '@/api/fetcher';
import { mergeCopyOnWrite } from '@/hooks/queryCache/mergeCopyOnWrite';
import { getNextCursor } from '@/hooks/queryCache/nextCursor';
import { prependItemToFirstPage, updateItemInPages } from '@/hooks/queryCache/pagedCache';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Messages, Message } from '@/shared/types/chat';
import { NewMessage } from '@/shared/types/rtm';

export type UpdateProps = {
  queryClient: QueryClient;
  message: Message;
  deskId: string;
  delivered_at?: string;
  read_at?: string;
};

export type UploadUrl = {
  upload_url: string;
  session_id: string;
  file_name: string;
  file_id: string;
};

const API = Object.freeze({
  listMessages: async (
    deskId: string,
    sessionId: string,
    cursor?: { cursor: string }
  ): Promise<Messages> => callGet(endpoints.messages(deskId, sessionId, cursor.cursor)),

  createMessage: async ({ deskId, sessionId, ...message }): Promise<Message> =>
    callPost(endpoints.message(deskId, sessionId), message),

  createPresignedUrl: async ({ deskId, sessionId, slug, ...rest }): Promise<UploadUrl> =>
    callPost(endpoints.uploadUrl(deskId, sessionId, slug), rest),
});

export const onMessageCreated = (queryClient: QueryClient, deskId: string, data: NewMessage) => {
  const queryKey = [endpoints.messages(deskId, data.session_id)];
  // A fetch still in flight may have started before this message, so fetch again.
  if (!queryClient.getQueryData(queryKey)) {
    queryClient.invalidateQueries({ queryKey });
    return;
  }
  queryClient.setQueryData<InfiniteData<Messages>>(queryKey, (prev) => {
    if (!prev) {
      return prev;
    }

    const [next, updated] = updateItemInPages<Messages, Message>(
      prev,
      'messages',
      (message) => message.message_id === data.message_id,
      (message) => mergeCopyOnWrite<Message>(message, data)
    );

    if (updated) {
      return next;
    }

    // Store a copy, never the socket payload itself.
    return prependItemToFirstPage(next, 'messages', cloneDeep(data) as Message);
  });
};

const useMessages = (deskId: string, sessionId?: string) => {
  const slug = useSelector(selectAccountSlug);
  const {
    data: messages,
    status: listStatus,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: [endpoints.messages(deskId, sessionId)],
    queryFn: async ({ pageParam }) => {
      return API.listMessages(deskId, sessionId, pageParam);
    },
    getNextPageParam: getNextCursor,
    initialPageParam: {} as { cursor: string | undefined },
    enabled: Boolean(deskId && sessionId),
    // messages:created reaches only the open conversation, so a cached thread refetches when shown.
    staleTime: 0,
    // A focus refetch reloads every loaded page; the open thread is already live.
    refetchOnWindowFocus: false,
  });

  const createPresignedUrl = async ({ file_id, file_type, original_name }) => {
    return API.createPresignedUrl({ deskId, sessionId, slug, file_id, file_type, original_name });
  };

  const { mutate: createMessage, status: createStatus } = useMutation<
    Message,
    Error,
    Partial<Message>
  >({
    mutationFn: (message) => {
      const { text, body } = message;
      return API.createMessage({ deskId, sessionId, text, body, timestamp: Date.now() });
    },
    meta: { errorToast: true },
  });

  return {
    messages,
    listStatus,
    createMessage,
    createStatus,
    fetchNextPage,
    hasNextPage,
    createPresignedUrl,
  };
};

export default useMessages;
